/** Tenant brand colour → runtime CSS variables (§3.1, §13.3).
 *
 * The default (#1957D6) uses the spec's exact hexes. A tenant colour derives dark/tint/ring
 * by mixing, and two contrast guards keep WCAG AA (§16.4) whatever the tenant picks:
 * - text on a brand fill switches to near-black when white would fail 4.5:1;
 * - brand-coloured text on white is darkened until it reaches 4.5:1. */

export const DEFAULT_PRIMARY = "#1957D6";

export type Rgb = [number, number, number];

export function parseHex(hex: string | null | undefined): Rgb | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export const toHex = ([r, g, b]: Rgb): string => `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}
export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
export function contrast(a: Rgb, b: Rgb): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
const INK: Rgb = [15, 23, 42]; // neutral-900

export type BrandVars = { brand: string; dark: string; tint: string; ring: string; on: string; text: string };

export function brandVars(primary: string | null | undefined): BrandVars {
  const rgb = parseHex(primary);
  if (!rgb || toHex(rgb) === DEFAULT_PRIMARY) {
    return { brand: "#1957D6", dark: "#123F9E", tint: "#EAF1FE", ring: "rgb(25 87 214 / 0.3)", on: "#FFFFFF", text: "#1957D6" };
  }
  let text = rgb;
  for (let i = 0; i < 20 && contrast(text, WHITE) < 4.5; i++) text = mix(text, BLACK, 0.12);
  return {
    brand: toHex(rgb),
    dark: toHex(mix(rgb, BLACK, 0.28)),
    tint: toHex(mix(rgb, WHITE, 0.9)),
    ring: `rgb(${rgb.map(Math.round).join(" ")} / 0.3)`,
    on: contrast(rgb, WHITE) >= 4.5 ? "#FFFFFF" : toHex(INK),
    text: toHex(text),
  };
}

/** The dark theme's card surface (tokens.css --surface in dark). */
const DARK_SURFACE: Rgb = [17, 26, 46];

/** Dark-theme variants of a tenant colour: brand text lightened until it reaches 4.5:1 on the
 * dark card, a deep tint for pills and selected rows, and a brighter focus ring. */
export function brandVarsDark(primary: string | null | undefined): { tint: string; ring: string; text: string } | null {
  const rgb = parseHex(primary);
  if (!rgb || toHex(rgb) === DEFAULT_PRIMARY) return null;
  let text = rgb;
  for (let i = 0; i < 24 && contrast(text, DARK_SURFACE) < 4.5; i++) text = mix(text, WHITE, 0.12);
  const ringBase = mix(rgb, WHITE, 0.35);
  return { tint: toHex(mix(rgb, DARK_SURFACE, 0.78)), ring: `rgb(${ringBase.map(Math.round).join(" ")} / 0.5)`, text: toHex(text) };
}

const TENANT_VARS = ["--tenant-brand", "--tenant-brand-dark", "--tenant-tint", "--tenant-ring", "--tenant-on", "--tenant-text", "--tenant-tint-d", "--tenant-ring-d", "--tenant-text-d"] as const;

/** Applies a tenant colour (or clears it for the BaraPOS default). The values go into
 * --tenant-* variables; tokens.css maps them to --brand* for the active light or dark theme. */
export function applyBrand(primary: string | null | undefined, root: HTMLElement = document.documentElement): void {
  const dark = brandVarsDark(primary);
  if (!dark) {
    for (const k of TENANT_VARS) root.style.removeProperty(k);
  } else {
    const v = brandVars(primary);
    const set: Record<(typeof TENANT_VARS)[number], string> = {
      "--tenant-brand": v.brand,
      "--tenant-brand-dark": v.dark,
      "--tenant-tint": v.tint,
      "--tenant-ring": v.ring,
      "--tenant-on": v.on,
      "--tenant-text": v.text,
      "--tenant-tint-d": dark.tint,
      "--tenant-ring-d": dark.ring,
      "--tenant-text-d": dark.text,
    };
    for (const k of TENANT_VARS) root.style.setProperty(k, set[k]);
  }
  syncThemeColorMeta();
}

/** <meta name="theme-color"> follows the page colour of the active theme (browser chrome). */
export function syncThemeColorMeta(): void {
  const dark = document.documentElement.dataset.theme === "dark";
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#0A1020" : "#F5F7FD");
}
